export function formatCurrency(
  value: Maybe<string> | undefined,
  maxDecimals?: number,
): string {
  const amount = value ? Number(value) : 0

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    ...(maxDecimals != null
      ? {
          maximumFractionDigits: maxDecimals,
          minimumFractionDigits: Math.min(maxDecimals, 2),
        }
      : {}),
  }).format(amount)
}

// Compact currency for stat strips. Precision drops as magnitude grows so
// every value fits 6 characters (7 with a minus): <$100 shows cents, $100–$999
// whole dollars, $1k–$99.9k one-decimal k, $100k–$999k whole k, then the
// pattern recurses at $1M. The bracket is chosen AFTER rounding so boundary
// values land in the larger bracket ($99.996 → "$100", $999.50 → "$1.0k").
export function formatCompactCurrency(value: number): string {
  const sign = value < 0 ? '-' : ''
  const abs = Math.abs(value)

  const cents = Math.round(abs * 100) / 100
  if (cents < 100) return `${sign}$${cents.toFixed(2)}`

  const dollars = Math.round(abs)
  if (dollars < 1000) return `${sign}$${dollars}`

  const tenthsOfK = Math.round(abs / 100) / 10
  if (tenthsOfK < 100) return `${sign}$${tenthsOfK.toFixed(1)}k`

  const thousands = Math.round(abs / 1000)
  if (thousands < 1000) return `${sign}$${thousands}k`

  const tenthsOfM = Math.round(abs / 100_000) / 10
  return `${sign}$${tenthsOfM.toFixed(1)}M`
}

// Axis ticks stay at max two decimals; trailing zeros are trimmed.
const chartTickFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})

export const formatTransactionChartYAxis = (value: number): string => {
  const thousands = value / 1000

  const isOverThousand = thousands >= 1

  return isOverThousand
    ? `$${chartTickFormat.format(thousands)}k`
    : `$${chartTickFormat.format(value)}`
}

export function formatPercentage(value: Maybe<string> | undefined): string {
  const amount = value ? Number(value) : 0

  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount / 100)
}

interface AddressLineParts {
  address1?: string | null
  address2?: string | null
  locality?: string | null
  administrativeArea?: string | null
  postalCode?: string | null
}

// Joins address parts into one display line, e.g.
// "123 Main St, Apt 4, Springfield, IL 60001". Strips the ISO "US-" prefix
// the BE stores on administrativeArea, and joins ZIP with a space (not comma).
export function formatAddressLine(address: AddressLineParts): string {
  const state = address.administrativeArea?.replace(/^US-/, '') ?? ''
  const street = [address.address1, address.address2].filter(Boolean).join(', ')
  const cityState = [address.locality, state].filter(Boolean).join(', ')
  const tail = [cityState, address.postalCode].filter(Boolean).join(' ')
  return [street, tail].filter(Boolean).join(', ')
}
